import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, TimeoutError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { readStoredToken } from '../shared/utils/session-token';

export interface ApiError {
  error: string;
  message: string;
  errors?: Array<{ field: string; message: string }>;
}

@Injectable({
  providedIn: 'root',
})
export class ApiClientService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiUrl;

  private readonly requestTimeout = 15000;

  /** Reads the stored token via shared utility (avoids circular dep with AuthService). */
  private getIdToken(): string | null {
    return readStoredToken();
  }

  /**
   * Get headers with authentication token if available
   */
  private getHeaders(customHeaders?: HttpHeaders): HttpHeaders {
    let headers = customHeaders || new HttpHeaders();
    const idToken = this.getIdToken();
    
    if (idToken) {
      headers = headers.set('Authorization', `Bearer ${idToken}`);
    }
    
    if (!headers.has('Content-Type')) {
      headers = headers.set('Content-Type', 'application/json');
    }
    
    return headers;
  }

  /**
   * Make a GET request
   */
  get<T>(endpoint: string, options?: { headers?: HttpHeaders }): Observable<T> {
    const headers = this.getHeaders(options?.headers);
    return this.http
        .get<T>(`${this.baseUrl}${endpoint}`, { ...options, headers })
        .pipe(timeout(this.requestTimeout), catchError(this.handleError.bind(this)));
  }

  /**
   * Make a POST request
   */
  post<T>(
      endpoint: string,
      body: any,
      options?: { headers?: HttpHeaders },
  ): Observable<T> {
    const headers = this.getHeaders(options?.headers);
    return this.http
        .post<T>(`${this.baseUrl}${endpoint}`, body, {
          ...options,
          headers,
        })
        .pipe(timeout(this.requestTimeout), catchError(this.handleError.bind(this)));
  }

  /**
   * Make a PUT request
   */
  put<T>(
      endpoint: string,
      body: any,
      options?: { headers?: HttpHeaders },
  ): Observable<T> {
    const headers = this.getHeaders(options?.headers);
    return this.http
        .put<T>(`${this.baseUrl}${endpoint}`, body, {
          ...options,
          headers,
        })
        .pipe(timeout(this.requestTimeout), catchError(this.handleError.bind(this)));
  }

  /**
   * Make a PATCH request
   */
  patch<T>(
      endpoint: string,
      body: any,
      options?: { headers?: HttpHeaders },
  ): Observable<T> {
    const headers = this.getHeaders(options?.headers);
    return this.http
        .patch<T>(`${this.baseUrl}${endpoint}`, body, {
          ...options,
          headers,
        })
        .pipe(timeout(this.requestTimeout), catchError(this.handleError.bind(this)));
  }

  /**
   * Make a DELETE request
   */
  delete<T>(endpoint: string, options?: { headers?: HttpHeaders }): Observable<T> {
    const headers = this.getHeaders(options?.headers);
    return this.http
        .delete<T>(`${this.baseUrl}${endpoint}`, { ...options, headers })
        .pipe(timeout(this.requestTimeout), catchError(this.handleError.bind(this)));
  }

  /**
   * Make an authenticated request with Firebase ID token
   */
  authenticatedGet<T>(endpoint: string, token: string): Observable<T> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
    return this.get<T>(endpoint, { headers });
  }

  /**
   * Make an authenticated POST request with Firebase ID token
   */
  authenticatedPost<T>(
      endpoint: string,
      body: any,
      token: string,
  ): Observable<T> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });
    return this.post<T>(endpoint, body, { headers });
  }

  /**
   * Make an authenticated PUT request with Firebase ID token
   */
  authenticatedPut<T>(
      endpoint: string,
      body: any,
      token: string,
  ): Observable<T> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });
    return this.put<T>(endpoint, body, { headers });
  }

  /**
   * Make an authenticated PATCH request with Firebase ID token
   */
  authenticatedPatch<T>(endpoint: string, body: any, token: string): Observable<T> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });
    return this.patch<T>(endpoint, body, { headers });
  }

  /**
   * Make an authenticated DELETE request with Firebase ID token
   */
  authenticatedDelete<T>(endpoint: string, token: string): Observable<T> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
    return this.delete<T>(endpoint, { headers });
  }

  /**
   * Handle HTTP errors and convert to user-friendly messages
   */
  private handleError(error: HttpErrorResponse | TimeoutError): Observable<never> {
    if (error instanceof TimeoutError) {
      return throwError(() => new Error('Request timed out. Please check your connection and try again.'));
    }

    let errorMessage = 'An unexpected error occurred';

    // ErrorEvent is browser-only; it does not exist in Node.js (SSR), so we must check typeof first
    if (typeof ErrorEvent !== 'undefined' && error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = error.error.message || 'Network error occurred';
    } else {
      // Server-side error
      const apiError = error.error as ApiError;
      if (apiError?.message) {
        errorMessage = apiError.message;
      } else if (apiError?.error) {
        errorMessage = apiError.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Handle specific HTTP status codes
      switch (error.status) {
        case 400:
          errorMessage = apiError?.message || 'Invalid request';
          break;
        case 401:
          errorMessage = 'Authentication failed. Please check your credentials.';
          break;
        case 403:
          errorMessage = 'Access denied. You do not have permission to perform this action.';
          break;
        case 404:
          errorMessage = 'Resource not found';
          break;
        case 500:
          errorMessage = 'Server error. Please try again later.';
          break;
        default:
          if (!apiError?.message) {
            errorMessage = `Error ${error.status}: ${error.statusText}`;
          }
      }
    }

    return throwError(() => new Error(errorMessage));
  }
}
